import { gql as apolloGql } from '@apollo/client';
import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { gql } from './__generated__';
import {
  QuestionAnswerType,
  TestAttemptModuleLevel,
  TestSectionModuleLevel,
} from './__generated__/graphql';
import type { Maybe } from './__generated__/graphql';

type AttemptUsersFilterInput = {
  courseId?: number;
  userSearch?: string;
};

type AttemptUsersPagingInput = {
  page: number;
  limit: number;
};

type AttemptUsersVariables = {
  input: { id: number };
  paging: AttemptUsersPagingInput;
  filter: AttemptUsersFilterInput;
};

type AttemptUsersResult = {
  limit?: number | null;
  page?: number | null;
  total?: number | null;
  results: {
    id: number;
    name?: string | null;
    email: string;
  }[];
};

type GetQuizAttemptUsersQuery = {
  quiz?: {
    getQuiz?: {
      attemptUsers?: AttemptUsersResult;
    };
  };
};

type GetTestAttemptUsersQuery = {
  test?: {
    getTest?: {
      attemptUsers?: AttemptUsersResult;
    };
  };
};

type LatestCompletedAttemptsVariables = {
  input: { id: number };
  attemptsFilter: { userIds: number[] };
};

type AttemptHistoryVariables = {
  input: { id: number };
  userId: number;
};

type AttemptByIdVariables = {
  input: { id: number };
};

export type AttemptCountItem = {
  userId: number;
  count: number;
};

export type AttemptHistoryItem = {
  id: number;
  createdAt: Date;
};

type AnswersAttemptUser = {
  id: number;
  name?: string | null;
  email: string;
};

type QuizViewAttemptQuestion = {
  questionId: number;
  text?: string | null;
  correct?: boolean | null;
  choice?: {
    order: number;
    correct?: boolean | null;
  } | null;
};

export type QuizViewAttempt = {
  id: number;
  createdAt: Date;
  score?: number | null;
  createdBy?: AnswersAttemptUser | null;
  questions: QuizViewAttemptQuestion[];
};

type AnswersQuestion = {
  id: number;
  domain?: { name: string } | null;
  skill?: { name: string } | null;
  type1?: { name: string } | null;
  subject?: { name: string } | null;
  choices: {
    id?: number;
    order: number;
    correct?: boolean | null;
    text?: string | null;
  }[];
  answerType: QuestionAnswerType;
};

type QuizViewQuestion = {
  order: number;
  question: AnswersQuestion;
};

type GetQuizViewDataQuery = {
  quiz: {
    getQuiz: {
      completedAttemptsCounts: AttemptCountItem[];
      latestCompletedAttempts: QuizViewAttempt[];
      quizQuestions: QuizViewQuestion[];
    };
  };
};

type GetQuizAttemptHistoryQuery = {
  quiz: {
    getQuiz: {
      completedAttemptsHistory: AttemptHistoryItem[];
    };
  };
};

type GetQuizAttemptByIdQuery = {
  quizAttempt: {
    getAttempt?: QuizViewAttempt | null;
  };
};

type GetQuizFirstAttemptsQuery = {
  quiz: {
    getQuiz: {
      firstCompletedAttempts: QuizViewAttempt[];
    };
  };
};

type TestViewAttemptQuestion = {
  id: number;
  correct?: boolean | null;
  questionId: number;
  text?: string | null;
  choice?: {
    id: number;
    order: number;
  } | null;
};

export type TestViewAttempt = {
  id: number;
  createdAt: Date;
  rwModuleLevel?: Maybe<TestAttemptModuleLevel>;
  mathModuleLevel?: Maybe<TestAttemptModuleLevel>;
  createdBy?: AnswersAttemptUser | null;
  questions: TestViewAttemptQuestion[];
};

type TestViewDataQuestion = {
  order: number;
  question: AnswersQuestion;
};

type TestViewDataModule = {
  id: number;
  level: TestSectionModuleLevel;
  order: number;
  questions: TestViewDataQuestion[];
};

type TestViewDataSection = {
  id: number;
  section: { name: string };
  modules: TestViewDataModule[];
};

type GetTestViewDataQuery = {
  test: {
    getTest: {
      sections: TestViewDataSection[];
      completedAttemptsCounts: AttemptCountItem[];
      latestCompletedAttempts: TestViewAttempt[];
    };
  };
};

type GetTestAttemptHistoryQuery = {
  test: {
    getTest: {
      completedAttemptsHistory: AttemptHistoryItem[];
    };
  };
};

type GetTestAttemptByIdQuery = {
  testAttempt: {
    getAttempt?: TestViewAttempt | null;
  };
};

type GetTestFirstAttemptsQuery = {
  test: {
    getTest: {
      firstCompletedAttempts: TestViewAttempt[];
    };
  };
};

export const GET_FILTERED_QUIZZES = gql(/* GraphQL */ `
  query getFilteredQuizzes(
    $filter: QuizFilterInput
    $paging: PagingInput
    $sort: QuizSortInput
  ) {
    quiz {
      getQuizzes(filter: $filter, paging: $paging, sort: $sort) {
        limit
        page
        total
        results {
          id
          name
        }
      }
    }
  }
`);

export const GET_FILTERED_TESTS = gql(/* GraphQL */ `
  query getFilteredTests(
    $filter: TestFilterInput
    $paging: PagingInput
    $sort: TestSortInput
  ) {
    test {
      getTests(filter: $filter, paging: $paging, sort: $sort) {
        limit
        page
        total
        results {
          id
          name
        }
      }
    }
  }
`);

export const GET_FILTERED_COURSES = gql(/* GraphQL */ `
  query getFilteredCourses(
    $paging: PagingInput
    $filter: CourseFilterInput
    $sort: CourseSortInput
  ) {
    course {
      getCourses(paging: $paging, filter: $filter, sort: $sort) {
        limit
        page
        total
        results {
          id
          name
        }
      }
    }
  }
`);

export const GET_QUIZ_ATTEMPT_USERS = apolloGql(/* GraphQL */ `
  query getQuizAttemptUsers(
    $input: GetQuizInput!
    $paging: PagingInput
    $filter: AttemptUsersFilterInput
  ) {
    quiz {
      getQuiz(input: $input) {
        attemptUsers(paging: $paging, filter: $filter) {
          limit
          page
          total
          results {
            id
            name
            email
          }
        }
      }
    }
  }
`) as unknown as TypedDocumentNode<
  GetQuizAttemptUsersQuery,
  AttemptUsersVariables
>;

export const GET_TEST_ATTEMPT_USERS = apolloGql(/* GraphQL */ `
  query getTestAttemptUsers(
    $input: GetTestInput!
    $paging: PagingInput
    $filter: AttemptUsersFilterInput
  ) {
    test {
      getTest(input: $input) {
        attemptUsers(paging: $paging, filter: $filter) {
          limit
          page
          total
          results {
            id
            name
            email
          }
        }
      }
    }
  }
`) as unknown as TypedDocumentNode<
  GetTestAttemptUsersQuery,
  AttemptUsersVariables
>;

export const GET_QUIZ_VIEW_DATA = apolloGql(/* GraphQL */ `
  query getQuizViewData(
    $input: GetQuizInput!
    $attemptsFilter: LatestCompletedAttemptsFilterInput!
  ) {
    quiz {
      getQuiz(input: $input) {
        completedAttemptsCounts(filter: $attemptsFilter) {
          userId
          count
        }
        latestCompletedAttempts(filter: $attemptsFilter) {
          id
          createdAt
          score
          createdBy {
            id
            name
            email
          }
          questions {
            questionId
            text
            correct
            choice {
              order
              correct
            }
          }
        }
        quizQuestions {
          order
          question {
            id
            domain {
              name
            }
            skill {
              name
            }
            type1 {
              name
            }
            subject {
              name
            }
            choices {
              order
              correct
              text
            }
            answerType
          }
        }
      }
    }
  }
`) as unknown as TypedDocumentNode<
  GetQuizViewDataQuery,
  LatestCompletedAttemptsVariables
>;

export const GET_QUIZ_ATTEMPT_HISTORY = apolloGql(/* GraphQL */ `
  query getQuizAttemptHistory($input: GetQuizInput!, $userId: Int!) {
    quiz {
      getQuiz(input: $input) {
        completedAttemptsHistory(userId: $userId) {
          id
          createdAt
        }
      }
    }
  }
`) as unknown as TypedDocumentNode<
  GetQuizAttemptHistoryQuery,
  AttemptHistoryVariables
>;

export const GET_QUIZ_ATTEMPT_BY_ID = apolloGql(/* GraphQL */ `
  query getQuizAttemptById($input: GetQuizAttemptInput!) {
    quizAttempt {
      getAttempt(input: $input) {
        id
        createdAt
        score
        createdBy {
          id
          name
          email
        }
        questions {
          questionId
          text
          correct
          choice {
            order
            correct
          }
        }
      }
    }
  }
`) as unknown as TypedDocumentNode<
  GetQuizAttemptByIdQuery,
  AttemptByIdVariables
>;

export const GET_QUIZ_FIRST_ATTEMPTS = apolloGql(/* GraphQL */ `
  query getQuizFirstAttempts(
    $input: GetQuizInput!
    $attemptsFilter: LatestCompletedAttemptsFilterInput!
  ) {
    quiz {
      getQuiz(input: $input) {
        firstCompletedAttempts(filter: $attemptsFilter) {
          id
          createdAt
          score
          createdBy {
            id
            name
            email
          }
          questions {
            questionId
            text
            correct
            choice {
              order
              correct
            }
          }
        }
      }
    }
  }
`) as unknown as TypedDocumentNode<
  GetQuizFirstAttemptsQuery,
  LatestCompletedAttemptsVariables
>;

export const GET_TEST_VIEW_DATA = apolloGql(/* GraphQL */ `
  query getTestViewData(
    $input: GetTestInput!
    $attemptsFilter: LatestCompletedAttemptsFilterInput!
  ) {
    test {
      getTest(input: $input) {
        sections {
          id
          section {
            name
          }
          modules {
            id
            level
            order
            questions {
              order
              question {
                id
                domain {
                  name
                }
                skill {
                  name
                }
                type1 {
                  name
                }
                subject {
                  name
                }
                choices {
                  id
                  order
                  correct
                  text
                }
                answerType
              }
            }
          }
        }
        completedAttemptsCounts(filter: $attemptsFilter) {
          userId
          count
        }
        latestCompletedAttempts(filter: $attemptsFilter) {
          id
          createdAt
          rwModuleLevel
          mathModuleLevel
          createdBy {
            id
            name
            email
          }
          questions {
            id
            correct
            questionId
            text
            choice {
              id
              order
            }
          }
        }
      }
    }
  }
`) as unknown as TypedDocumentNode<
  GetTestViewDataQuery,
  LatestCompletedAttemptsVariables
>;

export const GET_TEST_ATTEMPT_HISTORY = apolloGql(/* GraphQL */ `
  query getTestAttemptHistory($input: GetTestInput!, $userId: Int!) {
    test {
      getTest(input: $input) {
        completedAttemptsHistory(userId: $userId) {
          id
          createdAt
        }
      }
    }
  }
`) as unknown as TypedDocumentNode<
  GetTestAttemptHistoryQuery,
  AttemptHistoryVariables
>;

export const GET_TEST_ATTEMPT_BY_ID = apolloGql(/* GraphQL */ `
  query getTestAttemptById($input: GetTestAttemptInput!) {
    testAttempt {
      getAttempt(input: $input) {
        id
        createdAt
        rwModuleLevel
        mathModuleLevel
        createdBy {
          id
          name
          email
        }
        questions {
          id
          correct
          questionId
          text
          choice {
            id
            order
          }
        }
      }
    }
  }
`) as unknown as TypedDocumentNode<
  GetTestAttemptByIdQuery,
  AttemptByIdVariables
>;

export const GET_TEST_FIRST_ATTEMPTS = apolloGql(/* GraphQL */ `
  query getTestFirstAttempts(
    $input: GetTestInput!
    $attemptsFilter: LatestCompletedAttemptsFilterInput!
  ) {
    test {
      getTest(input: $input) {
        firstCompletedAttempts(filter: $attemptsFilter) {
          id
          createdAt
          rwModuleLevel
          mathModuleLevel
          createdBy {
            id
            name
            email
          }
          questions {
            id
            correct
            questionId
            text
            choice {
              id
              order
            }
          }
        }
      }
    }
  }
`) as unknown as TypedDocumentNode<
  GetTestFirstAttemptsQuery,
  LatestCompletedAttemptsVariables
>;
